/**
 * Kioskun tek durum kaynağı: misafirin o anki çekimi (foto → isim → stil → üretim) ve
 * etkinliğin hazır karakter koleksiyonu. Ekranlar bunu okur; kendi sahte durumlarını tutmaz.
 *
 * Durum akışı sunucudan gelir: SSE birincil, poll yedek (ADR-015). Olay kaybı misafiri
 * "hazırlanıyor" ekranında asılı bırakacağı için yedek kısılmaz.
 *
 * Durumun şekli ve okuma kapısı (`useKiosk`) `kiosk-context.ts`'te: bu dosya **yalnız bileşen**
 * dışa aktarır, yoksa Fast Refresh kırılır (#143).
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import type { OutfitId } from '../outfits'
import {
  createCharacter,
  deleteCharacter,
  fetchCharacter,
  fetchConfig,
  fetchReadyCharacters,
  subscribeToEvents,
  type CharacterStatus,
  type CreatedCharacter,
  type ShowcaseCharacter,
} from '../api/client'
import { KioskContext, type Capture } from './kiosk-context'

const POLL_INTERVAL_MS = 3000
const TERMINAL: readonly CharacterStatus[] = ['READY', 'FAILED']

export function KioskProvider({ children }: { children: ReactNode }) {
  const [eventId, setEventId] = useState<string | null>(null)
  const [capture, setCaptureState] = useState<Capture | null>(null)
  const [displayName, setDisplayName] = useState('')
  const [outfitId, setOutfitId] = useState<OutfitId | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [created, setCreated] = useState<CreatedCharacter | null>(null)
  const [status, setStatus] = useState<CharacterStatus | null>(null)
  const [failureReason, setFailureReason] = useState<string | null>(null)
  const [collection, setCollection] = useState<ShowcaseCharacter[]>([])
  const [newestCharacterId, setNewestCharacterId] = useState<string | null>(null)
  const [streamConnected, setStreamConnected] = useState(false)

  // Ref'ler **effect'te** yazılır: render sırasında ref'e dokunmak React'in saflık kuralını kırar
  const captureRef = useRef<Capture | null>(null)
  const createdIdRef = useRef<string | null>(null)
  useEffect(() => {
    captureRef.current = capture
  }, [capture])
  useEffect(() => {
    createdIdRef.current = created?.characterId ?? null
  }, [created])

  // ── Açılış: etkinlik kimliği sunucudan, koleksiyon ilk yüklemede ────────────
  useEffect(() => {
    const ac = new AbortController()
    void (async () => {
      try {
        const config = await fetchConfig(ac.signal)
        setEventId(config.eventId)
      } catch {
        // api henüz ayakta değil: ekran çalışmaya devam eder, koleksiyon boş kalır
      }
    })()
    return () => ac.abort()
  }, [])

  const loadCollection = useCallback(
    async (signal?: AbortSignal): Promise<ShowcaseCharacter[] | null> => {
      if (!eventId) return null
      try {
        const rows = await fetchReadyCharacters(eventId, signal)
        // API `updated_at` artan sırada döner (backend'in kasıtlı sözleşmesi --
        // `updatedAfter` artımlı sorgusu buna dayanır, ListReadyCharacters.execute).
        // Görüntüleme sırası bundan bağımsız bir karar: galeri en yeni karakteri en
        // önde göstermeli (etkinlikte defalarca yaşanan sorun -- yeni oluşan
        // karakter listenin dibine gömülüp bulunamıyordu). Sadece burada, sadece
        // ekrana yansıyan sırayı çeviriyoruz; API'ye giden `updatedAfter` cursor'ı
        // ve backend'in kendi sıralaması dokunulmadan kalıyor.
        return [...rows].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
      } catch {
        return null // geçici hata: bir sonraki olay/poll düzeltir
      }
    },
    [eventId],
  )

  useEffect(() => {
    const ac = new AbortController()
    void (async () => {
      const rows = await loadCollection(ac.signal)
      if (rows) setCollection(rows)
    })()
    return () => ac.abort()
  }, [loadCollection])

  const dropFromCollection = useCallback((characterId: string) => {
    setCollection((rows) => rows.filter((row) => row.characterId !== characterId))
    setNewestCharacterId((id) => (id === characterId ? null : id))
  }, [])

  // ── SSE: hem koleksiyon hem o anki üretim aynı akıştan beslenir ─────────────
  useEffect(() => {
    if (!eventId) return
    return subscribeToEvents(
      eventId,
      (event) => {
        if (event.status === 'READY') {
          setNewestCharacterId(event.characterId)
          void (async () => {
            const rows = await loadCollection()
            if (rows) setCollection(rows)
          })()
        }
        if (createdIdRef.current === event.characterId) {
          setStatus(event.status)
          setFailureReason(event.failureReason ?? null)
        }
      },
      setStreamConnected,
      // Silme başka bir yüzeyden de gelebilir; ayrıca listeyi yeniden çekmek silineni
      // **öğretmez**: `updatedAfter` artımlı sorgusu yalnız var olanı döndürür (#94).
      (event) => dropFromCollection(event.characterId),
    )
  }, [eventId, loadCollection, dropFromCollection])

  // ── Poll yedeği: SSE koptuysa/olay düştüyse misafir ekranda asılı kalmaz ────
  useEffect(() => {
    const characterId = created?.characterId
    if (!characterId || (status && TERMINAL.includes(status))) return
    const ac = new AbortController()
    const timer = setInterval(() => {
      void (async () => {
        try {
          const state = await fetchCharacter(characterId, ac.signal)
          setStatus(state.status)
          setFailureReason(state.failureReason ?? null)
        } catch {
          // ağ hatası: bir sonraki tur dener
        }
      })()
    }, POLL_INTERVAL_MS)
    return () => {
      ac.abort()
      clearInterval(timer)
    }
  }, [created, status])

  // Önizleme **türetilir**, ayrı durum tutulmaz: koleksiyon neyi diyorsa o
  const previewUrl = useMemo(
    () =>
      created
        ? (collection.find((c) => c.characterId === created.characterId)?.previewUrl ?? null)
        : null,
    [created, collection],
  )

  const setCapture = useCallback((next: Capture) => {
    const previous = captureRef.current
    if (previous) URL.revokeObjectURL(previous.url)
    setCaptureState(next)
  }, [])

  const submit = useCallback(async () => {
    if (!capture || !outfitId || submitting) return null
    setSubmitting(true)
    setSubmitError(null)
    try {
      const result = await createCharacter({
        photo: capture.blob,
        displayName: displayName.trim(),
        outfitId,
        region: capture.region,
      })
      setCreated(result)
      setStatus(result.status)
      return result
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'İstek gönderilemedi')
      return null
    } finally {
      setSubmitting(false)
    }
  }, [capture, displayName, outfitId, submitting])

  const removeCharacter = useCallback(
    async (characterId: string) => {
      const snapshot = collection
      dropFromCollection(characterId)
      try {
        await deleteCharacter(characterId)
        return true
      } catch {
        setCollection(snapshot) // sunucu silmedi: kart geri gelsin, misafir yanılmasın
        return false
      }
    },
    [collection, dropFromCollection],
  )

  const reset = useCallback(() => {
    if (captureRef.current) URL.revokeObjectURL(captureRef.current.url)
    setCaptureState(null)
    setDisplayName('')
    setOutfitId(null)
    setCreated(null)
    setStatus(null)
    setFailureReason(null)
    setSubmitError(null)
  }, [])

  const value = useMemo(
    () => ({
      eventId,
      capture,
      displayName,
      outfitId,
      submitting,
      submitError,
      created,
      status,
      failureReason,
      previewUrl,
      collection,
      newestCharacterId,
      streamConnected,
      setCapture,
      setDisplayName,
      setOutfitId,
      submit,
      reset,
      removeCharacter,
    }),
    [
      eventId,
      capture,
      displayName,
      outfitId,
      submitting,
      submitError,
      created,
      status,
      failureReason,
      previewUrl,
      collection,
      newestCharacterId,
      streamConnected,
      setCapture,
      submit,
      reset,
      removeCharacter,
    ],
  )

  return <KioskContext value={value}>{children}</KioskContext>
}
